<script setup lang="ts">
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldPartTemplate } from "@/types/field-observations.types";
import { useGetFieldPartTemplates } from "@/stores/queries/field-observations.query";
import { useApplySectionTemplate } from "@/stores/queries/sections.query";
import FieldPartTemplateTreePreview from "@/components/field-part-templates/FieldPartTemplateTreePreview.vue";
import DRBtn from "@/components/UI/DRBtn.vue";

const props = defineProps<{
  modelValue: boolean;
  reportId: number;
  parentSectionId?: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "inserted"): void;
}>();

const { t } = useTranslation();

const { data: templates, isLoading } = useGetFieldPartTemplates();
const { mutate: applyTemplate, isPending } = useApplySectionTemplate();

const selectedTemplateId = ref<number | null>(null);

const selectedTemplate = computed<FieldPartTemplate | undefined>(() => {
  if (!selectedTemplateId.value || !templates.value) return undefined;
  return templates.value.find((t) => t.id === selectedTemplateId.value);
});

const show = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const close = () => {
  selectedTemplateId.value = null;
  show.value = false;
};

const handleInsert = () => {
  if (!selectedTemplateId.value) return;

  applyTemplate(
    {
      reportId: props.reportId,
      templateId: selectedTemplateId.value,
      parentSectionId: props.parentSectionId,
    },
    {
      onSuccess: () => {
        emit("inserted");
        close();
      },
    },
  );
};
</script>

<template>
  <el-dialog
    v-model="show"
    :title="t('report:photos.sections.templates.insertTitle')"
    width="90vw"
    top="5vh"
    append-to-body
    class="insert-template-dialog"
  >
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="d-flex align-items-center justify-content-center py-5"
    >
      <span class="spinner-border spinner-border-sm me-2" />
      {{ t("common:loading") }}
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!templates || templates.length === 0"
      class="d-flex align-items-center justify-content-center py-5 text-muted"
    >
      <div class="text-center">
        <i class="bi-folder-open fs-1 d-block mb-2 opacity-50"></i>
        <p class="mb-0">
          {{ t("report:photos.sections.templates.noTemplates") }}
        </p>
      </div>
    </div>

    <!-- Split layout: list + preview -->
    <div v-else class="split-layout">
      <!-- Left: templates list -->
      <div class="templates-panel">
        <div class="templates-list">
          <div
            v-for="template in templates"
            :key="template.id"
            class="template-option"
            :class="{ selected: selectedTemplateId === template.id }"
            @click="selectedTemplateId = template.id"
          >
            <div class="template-radio">
              <div
                v-if="selectedTemplateId === template.id"
                class="radio-dot"
              />
            </div>
            <div class="template-info">
              <span class="template-name">{{ template.name }}</span>
              <span v-if="template.description" class="template-description">
                {{ template.description }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: preview -->
      <div class="preview-panel">
        <div v-if="selectedTemplate" class="preview-content">
          <div class="preview-label">
            <i class="bi-eye me-1"></i>
            {{ t("report:photos.sections.templates.preview") }}
          </div>
          <div class="preview-tree">
            <FieldPartTemplateTreePreview
              :nodes="selectedTemplate.tree.parts"
            />
          </div>
        </div>
        <div v-else class="preview-empty">
          <i class="bi-diagram-3 fs-1 opacity-25"></i>
          <p class="text-muted small mt-2 mb-0">
            {{ t("report:photos.sections.templates.preview") }}
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="d-flex gap-2 justify-content-end">
        <DRBtn :label="t('cancel')" variant="outline" @click="close" />
        <DRBtn
          :label="t('report:photos.sections.templates.insert')"
          variant="primary"
          :disabled="!selectedTemplateId"
          :loading="isPending"
          @click="handleInsert"
        />
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.split-layout {
  display: flex;
  gap: 20px;
  height: 65vh;
  min-height: 300px;
}

.templates-panel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.templates-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
}

.template-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 2px solid var(--bs-border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    border-color: var(--bs-primary);
  }

  &.selected {
    border-color: var(--bs-primary);
    background-color: rgba(var(--bs-primary-rgb), 0.05);
  }
}

.template-radio {
  width: 20px;
  height: 20px;
  border: 2px solid var(--bs-border-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;

  .selected & {
    border-color: var(--bs-primary);
  }

  .radio-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--bs-primary);
  }
}

.template-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.template-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--bs-body-color);
}

.template-description {
  font-size: 13px;
  color: var(--bs-secondary-color);
}

.preview-panel {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.preview-label {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background-color: var(--bs-tertiary-bg);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
  flex-shrink: 0;
}

.preview-tree {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
}

.preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--bs-secondary-color);
}
</style>

<style lang="scss">
.insert-template-dialog {
  max-width: 900px;

  .el-dialog__body {
    padding-top: 10px;
    padding-bottom: 10px;
  }
}
</style>
